import { z } from 'zod';

import { Id, IsoDateTime } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const SupportTicketStatusSchema = z.enum([
  'OPEN',
  'ASSIGNED',
  'IN_PROGRESS',
  'WAITING_FOR_CUSTOMER',
  'PENDING_CUSTOMER', // legacy alias of WAITING_FOR_CUSTOMER
  'ESCALATED',
  'RESOLVED',
  'CLOSED',
]);
export type SupportTicketStatus = z.infer<typeof SupportTicketStatusSchema>;

export const SUPPORT_TICKET_STATUSES = SupportTicketStatusSchema.options;

export const SupportTicketPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
export type SupportTicketPriority = z.infer<typeof SupportTicketPrioritySchema>;

export const SupportTicketCategorySchema = z.enum([
  // Marketplace-v2 categories — preferred for all new tickets.
  'ORDER_ISSUE',
  'PAYMENT_ISSUE',
  'REFUND_ISSUE',
  'RETURN_REQUEST',
  'WRONG_PRODUCT',
  'DAMAGED_PRODUCT',
  'DELIVERY_DELAY',
  'TECHNICAL_ISSUE',
  'OTHER',
  // Legacy values retained so old rows still validate.
  'ORDER',
  'PAYMENT',
  'DELIVERY',
  'RETURN_REFUND',
  'ACCOUNT',
  'PRODUCT',
  'VENDOR',
]);
export type SupportTicketCategory = z.infer<typeof SupportTicketCategorySchema>;

/// Categories presented to customers when creating a new ticket.
export const CUSTOMER_TICKET_CATEGORIES = [
  'ORDER_ISSUE',
  'PAYMENT_ISSUE',
  'REFUND_ISSUE',
  'RETURN_REQUEST',
  'WRONG_PRODUCT',
  'DAMAGED_PRODUCT',
  'DELIVERY_DELAY',
  'TECHNICAL_ISSUE',
  'OTHER',
] as const satisfies readonly SupportTicketCategory[];

export const TICKET_CATEGORY_LABELS: Record<string, string> = {
  ORDER_ISSUE: 'Order Issue',
  PAYMENT_ISSUE: 'Payment Issue',
  REFUND_ISSUE: 'Refund Issue',
  RETURN_REQUEST: 'Return Request',
  WRONG_PRODUCT: 'Wrong Product',
  DAMAGED_PRODUCT: 'Damaged Product',
  DELIVERY_DELAY: 'Delivery Delay',
  TECHNICAL_ISSUE: 'Technical Issue',
  OTHER: 'Other',
  ORDER: 'Order (legacy)',
  PAYMENT: 'Payment (legacy)',
  DELIVERY: 'Delivery (legacy)',
  RETURN_REFUND: 'Return / Refund (legacy)',
  ACCOUNT: 'Account (legacy)',
  PRODUCT: 'Product (legacy)',
  VENDOR: 'Vendor (legacy)',
};

export const SupportActivityKindSchema = z.enum([
  'CREATED',
  'ASSIGNED',
  'STATUS_CHANGED',
  'PRIORITY_CHANGED',
  'REPLY_CUSTOMER',
  'REPLY_VENDOR',
  'REPLY_ADMIN',
  'ATTACHMENT_ADDED',
  'ESCALATED',
  'RESOLVED',
  'REOPENED',
  'CLOSED',
  'SLA_BREACHED',
  'NOTE_INTERNAL',
]);
export type SupportActivityKind = z.infer<typeof SupportActivityKindSchema>;

// ── Response shapes ────────────────────────────────────────────────────────

export const SupportTicketAttachmentSchema = z.object({
  id: Id,
  ticketId: Id,
  messageId: Id.nullable(),
  uploaderId: Id,
  url: z.string(),
  filename: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  createdAt: IsoDateTime,
});
export type SupportTicketAttachment = z.infer<typeof SupportTicketAttachmentSchema>;

export const SupportTicketActivityLogSchema = z.object({
  id: Id,
  ticketId: Id,
  actorId: Id.nullable(),
  actorRole: z.string(),
  kind: SupportActivityKindSchema,
  payload: z.unknown().nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type SupportTicketActivityLog = z.infer<typeof SupportTicketActivityLogSchema>;

export const SupportTicketMessageSchema = z.object({
  id: Id,
  ticketId: Id,
  authorId: Id,
  authorRole: z.string(),
  body: z.string(),
  attachments: z.unknown().nullable(),
  internal: z.boolean(),
  createdAt: IsoDateTime,
});
export type SupportTicketMessage = z.infer<typeof SupportTicketMessageSchema>;

export const SupportTicketSchema = z.object({
  id: Id,
  ticketNumber: z.string(),
  subjectUserId: Id,
  assignedToId: Id.nullable(),
  vendorId: Id.nullable(),
  orderGroupId: Id.nullable(),
  orderItemId: Id.nullable(),
  productId: Id.nullable(),
  category: SupportTicketCategorySchema,
  subject: z.string(),
  description: z.string().nullable(),
  status: SupportTicketStatusSchema,
  priority: SupportTicketPrioritySchema,
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  tags: z.array(z.string()).default([]),
  slaDueAt: IsoDateTime.nullable(),
  slaBreachedAt: IsoDateTime.nullable(),
  firstResponseAt: IsoDateTime.nullable(),
  resolvedAt: IsoDateTime.nullable(),
  escalatedAt: IsoDateTime.nullable(),
  closedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type SupportTicket = z.infer<typeof SupportTicketSchema>;

export const SupportTicketDetailSchema = SupportTicketSchema.extend({
  messages: z.array(SupportTicketMessageSchema),
  attachments: z.array(SupportTicketAttachmentSchema),
  activityLogs: z.array(SupportTicketActivityLogSchema),
});
export type SupportTicketDetail = z.infer<typeof SupportTicketDetailSchema>;

// ── Request shapes ─────────────────────────────────────────────────────────

export const TicketAttachmentInput = z
  .object({
    fileId: Id.optional(),
    url: z.string().trim().min(1).max(500),
    filename: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().max(120).optional(),
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(50 * 1024 * 1024)
      .optional(),
  })
  .strict();
export type TicketAttachmentInput = z.infer<typeof TicketAttachmentInput>;

export const CreateTicketRequest = z
  .object({
    category: SupportTicketCategorySchema,
    subject: z.string().trim().min(4).max(200),
    body: z.string().trim().min(4).max(8000),
    priority: SupportTicketPrioritySchema.optional(),
    /// Soft reference fields (legacy support).
    refType: z.string().trim().max(64).optional(),
    refId: z.string().trim().max(64).optional(),
    /// Marketplace-v2 scoping. When `productId` or `orderItemId` is provided the
    /// service auto-resolves the owning vendor and rejects creation if the product
    /// cannot be matched to a vendor.
    orderGroupId: Id.optional(),
    orderItemId: Id.optional(),
    productId: Id.optional(),
    /// Vendor may be supplied by the caller (admin scenario); customers leave it
    /// blank and the service derives it from product/order item.
    vendorId: Id.optional(),
    attachments: z.array(TicketAttachmentInput).max(10).optional(),
  })
  .strict();
export type CreateTicketRequest = z.infer<typeof CreateTicketRequest>;

export const AddTicketMessageRequest = z
  .object({
    body: z.string().trim().min(1).max(8000),
    /// New structured attachment input (preferred). Each attachment is persisted
    /// to the `SupportTicketAttachment` table and linked to the resulting message.
    attachments: z.array(TicketAttachmentInput).max(10).optional(),
    /// Admin/agent/vendor-only field — customers cannot post internal notes.
    internal: z.boolean().optional(),
  })
  .strict();
export type AddTicketMessageRequest = z.infer<typeof AddTicketMessageRequest>;

export const UpdateTicketRequest = z
  .object({
    status: SupportTicketStatusSchema.optional(),
    priority: SupportTicketPrioritySchema.optional(),
    assignedToId: Id.nullable().optional(),
    vendorId: Id.nullable().optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  })
  .strict();
export type UpdateTicketRequest = z.infer<typeof UpdateTicketRequest>;

export const EscalateTicketRequest = z
  .object({
    reason: z.string().trim().min(4).max(500),
    priority: SupportTicketPrioritySchema.optional(),
  })
  .strict();
export type EscalateTicketRequest = z.infer<typeof EscalateTicketRequest>;

export const VendorResolveTicketRequest = z
  .object({
    resolutionNote: z.string().trim().min(4).max(2000),
  })
  .strict();
export type VendorResolveTicketRequest = z.infer<typeof VendorResolveTicketRequest>;

export const ListTicketsQuery = z
  .object({
    status: SupportTicketStatusSchema.optional(),
    priority: SupportTicketPrioritySchema.optional(),
    category: SupportTicketCategorySchema.optional(),
    assignedToId: Id.optional(),
    vendorId: Id.optional(),
    orderGroupId: Id.optional(),
    productId: Id.optional(),
    /// Substring search across ticketNumber + subject. Server lowercases.
    q: z.string().trim().max(100).optional(),
    slaState: z.enum(['BREACHED', 'AT_RISK', 'HEALTHY']).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type ListTicketsQuery = z.infer<typeof ListTicketsQuery>;

// ── Vendor performance / SLA monitoring ─────────────────────────────────────

export const VendorSupportMetrics = z.object({
  vendorId: Id,
  vendorName: z.string().nullable(),
  openCount: z.number(),
  inProgressCount: z.number(),
  resolvedCount: z.number(),
  escalatedCount: z.number(),
  slaBreachedCount: z.number(),
  avgFirstResponseMinutes: z.number().nullable(),
  avgResolutionHours: z.number().nullable(),
});
export type VendorSupportMetrics = z.infer<typeof VendorSupportMetrics>;

export const SupportFaq = z.object({
  id: z.string(),
  category: z.string(),
  question: z.string(),
  answer: z.string(),
});
export type SupportFaq = z.infer<typeof SupportFaq>;

export const SupportHelpCategory = z.object({
  key: SupportTicketCategorySchema,
  label: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
});
export type SupportHelpCategory = z.infer<typeof SupportHelpCategory>;

export const SupportContactInfo = z.object({
  email: z.string(),
  phone: z.string().nullable(),
  hours: z.string().nullable(),
  address: z.string().nullable(),
});
export type SupportContactInfo = z.infer<typeof SupportContactInfo>;

// ── Product Queries & Conversations ──────────────────────────────────────────

export const ProductQuerySchema = z.object({
  id: Id,
  productId: Id,
  customerId: Id,
  question: z.string(),
  answer: z.string().nullable(),
  answeredById: Id.nullable(),
  vendorId: Id.nullable(),
  status: z.enum(['PENDING', 'PUBLISHED', 'REJECTED']),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ProductQuery = z.infer<typeof ProductQuerySchema>;

/** What anyone browsing a product sees: no asker, answerer or seller ids. */
export const PublicProductQuerySchema = ProductQuerySchema.pick({
  id: true,
  question: true,
  answer: true,
  createdAt: true,
  updatedAt: true,
});
export type PublicProductQuery = z.infer<typeof PublicProductQuerySchema>;

export const CreateProductQueryRequest = z.object({
  productId: Id,
  question: z.string().trim().min(5).max(1000),
});
export type CreateProductQueryRequest = z.infer<typeof CreateProductQueryRequest>;

export const AnswerProductQueryRequest = z.object({
  answer: z.string().trim().min(1).max(2000),
  status: z.enum(['PUBLISHED', 'REJECTED']).optional(),
});
export type AnswerProductQueryRequest = z.infer<typeof AnswerProductQueryRequest>;

export const ProductConversationStatusSchema = z.enum(['OPEN', 'RESOLVED', 'CLOSED']);
export type ProductConversationStatus = z.infer<typeof ProductConversationStatusSchema>;

export const ProductConversationMessageSchema = z.object({
  id: Id,
  conversationId: Id,
  senderId: Id,
  body: z.string(),
  isRead: z.boolean(),
  createdAt: IsoDateTime,
});
export type ProductConversationMessage = z.infer<typeof ProductConversationMessageSchema>;

export const ProductConversationSchema = z.object({
  id: Id,
  productId: Id,
  buyerId: Id,
  vendorId: Id,
  subject: z.string(),
  status: ProductConversationStatusSchema,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ProductConversation = z.infer<typeof ProductConversationSchema>;

export const ProductConversationDetailSchema = ProductConversationSchema.extend({
  messages: z.array(ProductConversationMessageSchema),
});
export type ProductConversationDetail = z.infer<typeof ProductConversationDetailSchema>;

export const CreateProductConversationRequest = z.object({
  productId: Id,
  subject: z.string().trim().min(5).max(200),
  initialMessage: z.string().trim().min(1).max(4000),
});
export type CreateProductConversationRequest = z.infer<typeof CreateProductConversationRequest>;

export const ReplyProductConversationRequest = z.object({
  body: z.string().trim().min(1).max(4000),
});
export type ReplyProductConversationRequest = z.infer<typeof ReplyProductConversationRequest>;

// ── Contact Messages ──────────────────────────────────────────────────────────

export const ContactMessageSchema = z.object({
  id: Id,
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  message: z.string(),
  status: z.string(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ContactMessage = z.infer<typeof ContactMessageSchema>;

export const CreateContactMessageRequest = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email().max(100),
  phone: z.string().trim().max(15).optional().nullable(),
  message: z.string().trim().min(5).max(4000),
});
export type CreateContactMessageRequest = z.infer<typeof CreateContactMessageRequest>;

export const UpdateContactMessageStatusRequest = z.object({
  status: z.enum(['PENDING', 'READ', 'REPLIED']),
});
export type UpdateContactMessageStatusRequest = z.infer<typeof UpdateContactMessageStatusRequest>;
